import { useLocation } from "@solidjs/router";
import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { createMemo, createResource, Show } from "solid-js";
import type { PageMapLens } from "../../enums/map";
import { PAGE_MAP_LENS } from "../../enums/map";
import type { QuickLookEmphasis } from "../../enums/scan";
import { QUICK_LOOK_EMPHASIS } from "../../enums/scan";
import { api } from "../../store/api";
import { Fact } from "../../ui/Fact";
import {
  pageDisplayLabel,
  rawPagePath,
} from "../pages/boundaries/page-label.parser";
import { clusterLabel } from "./estateFormat";
import { structuralConnectionsForPage } from "./estateRelationships";
import { connectionLabels } from "./estateText";
import { lensLabel } from "./estateViewport";
import { clientIssueTitle, issueDimensionLabel } from "./issueText";
import { pageDetailHref } from "./pageDetailNavigation";
import {
  actionForPage,
  buildPageContext,
  ownerForIssue,
  pageContextImpact,
  pageContextLabel,
} from "./scanExperienceModel";
import {
  clusterContextForPages,
  clusterKey,
  pageContentFitnessCoverageLabel,
  pageContentFitnessScore,
  pageHealthClass,
} from "./scanModel";

export function PageQuickLookPanel(props: {
  data: OverviewResponse;
  websiteId: string;
  page: OverviewResponse["pages"][number];
  lens?: PageMapLens;
}) {
  const [detail] = createResource(
    () => props.page.id,
    (pageId) => api.pageDetail(props.websiteId, pageId),
  );
  const context = createMemo(() =>
    buildPageContext(
      props.data,
      props.page,
      detail(),
      props.lens ?? PAGE_MAP_LENS.HUMAN,
    ),
  );
  const topIssue = () => context().topIssue;
  const connections = createMemo(() =>
    structuralConnectionsForPage(props.data, props.page.id),
  );
  const clusterContext = createMemo(() =>
    clusterContextForPages(props.data.pages),
  );
  const pageClusterLabel = createMemo(() => {
    const key = clusterKey(props.page.path, clusterContext());
    return clusterLabel(
      key,
      props.data.pages.filter(
        (page) => clusterKey(page.path, clusterContext()) === key,
      ),
    );
  });
  const activeScore = () =>
    (props.lens ?? PAGE_MAP_LENS.HUMAN) === PAGE_MAP_LENS.AGENT
      ? props.page.agentScore
      : pageContentFitnessScore(props.page);
  const location = useLocation();
  const pagesReturnQuery = () => {
    const returnParams = new URLSearchParams(location.search);
    returnParams.set("page", props.page.id);
    returnParams.set("lens", props.lens ?? "human");
    return `?${returnParams.toString()}`;
  };
  const detailHref = () =>
    pageDetailHref(props.websiteId, props.page.id, {
      source: "pages",
      returnQuery: pagesReturnQuery(),
    });
  const issueHref = (issueId: string) => {
    const params = new URLSearchParams({ from: "pages" });
    params.set("return", pagesReturnQuery());
    return `/w/${props.websiteId}/scan/issues/${issueId}?${params.toString()}`;
  };

  return (
    <aside class="signal-selected-panel" aria-label="Selected page quick look">
      <div class="signal-selected-action-bar">
        <span>Selected page</span>
        <a class="signal-primary-link" href={detailHref()}>
          Open page detail
        </a>
      </div>
      <div class="signal-selected-head">
        <div>
          <span class="signal-mini-kicker">{pageContextLabel(context())}</span>
          <h2>{pageDisplayLabel(props.page, props.data.site)}</h2>
          <p class="mono">{rawPagePath(props.page)}</p>
        </div>
        <span class={`signal-state-pill is-${pageHealthClass(activeScore())}`}>
          {pageHealthClass(activeScore())}
        </span>
      </div>

      <div class="signal-score-pair signal-page-score-pair">
        <ScoreTile
          emphasis={QUICK_LOOK_EMPHASIS.PRIMARY}
          label="Content Fitness"
          value={pageContentFitnessScore(props.page)}
          detail={pageContentFitnessCoverageLabel(props.page)}
        />
        <ScoreTile
          emphasis={QUICK_LOOK_EMPHASIS.SUPPORTING}
          label="Agent diagnostic"
          value={props.page.agentScore}
          detail="Separate from Content Fitness"
        />
      </div>

      <section class="signal-context-card">
        <h3>Why this page matters</h3>
        <p>{pageContextImpact(context())}</p>
        <div class="signal-tag-row">
          <Show when={context().strategic}>
            <span>Linked to a priority issue</span>
          </Show>
          <Show when={context().weak}>
            <span>
              Needs attention in {lensLabel(props.lens ?? PAGE_MAP_LENS.HUMAN)}
            </span>
          </Show>
          <Show when={topIssue()}>
            {(issue) => <span>{issueDimensionLabel(issue())}</span>}
          </Show>
        </div>
      </section>

      <section class="signal-context-card signal-page-connections">
        <h3>Site structure</h3>
        <dl class="signal-mini-facts">
          <Fact label="Cluster" value={pageClusterLabel()} />
          <Fact
            label="Connected pages"
            value={`${connections().connected.length}`}
          />
        </dl>
        <Show
          when={connections().connected.length > 0}
          fallback={
            <p>No structural links were recorded for this page in this scan.</p>
          }
        >
          <div class="signal-connection-summary">
            <Show when={connections().inbound.length > 0}>
              <p>
                <strong>Inbound structural links</strong>
                {connectionLabels(connections().inbound, props.data.site)}
              </p>
            </Show>
            <Show when={connections().outbound.length > 0}>
              <p>
                <strong>Outbound structural links</strong>
                {connectionLabels(connections().outbound, props.data.site)}
              </p>
            </Show>
          </div>
        </Show>
      </section>

      <section class="signal-context-card">
        <h3>Evidence availability</h3>
        <dl class="signal-mini-facts">
          <Fact label="Linked issues" value={`${context().issues.length}`} />
          <Fact
            label="Screenshots"
            value={
              context().screenshotCount === null
                ? "Checking"
                : `${context().screenshotCount}`
            }
          />
          <Fact
            label="Evidence"
            value={
              context().hasEvidence === null
                ? "Checking"
                : context().hasEvidence
                  ? "Available"
                  : "Not stored"
            }
          />
        </dl>
      </section>

      <section class="signal-context-card">
        <h3>Top issue</h3>
        <Show
          when={topIssue()}
          keyed
          fallback={<p>No open issue is linked directly to this page.</p>}
        >
          {(issue) => (
            <>
              <strong>{clientIssueTitle(issue)}</strong>
              <p>
                {issueDimensionLabel(issue)} / owner: {ownerForIssue(issue)}
              </p>
              <a class="signal-inline-link" href={issueHref(issue.id)}>
                Open linked issue
              </a>
            </>
          )}
        </Show>
      </section>

      <section class="signal-context-card">
        <h3>Recommended action</h3>
        <p>{actionForPage(context())}</p>
      </section>
    </aside>
  );
}

function ScoreTile(props: {
  emphasis: QuickLookEmphasis;
  label: string;
  value: number | null;
  detail: string;
}) {
  return (
    <div
      class={`signal-score-tile is-${pageHealthClass(props.value)} is-${props.emphasis}`}
    >
      <span>{props.label}</span>
      <strong>{props.value ?? "--"}</strong>
      <small>{props.detail}</small>
    </div>
  );
}
